<template>
  <v-container id="one-on-one" fluid tag="section" class="dashboard-container" style="padding: 24px 96px;">
    <v-row class="mb-2 ma-n2" align="center">
      <v-col cols="12" sm="auto" class="pa-2">
        <div class="d-flex align-center">
          <v-icon color="#00E5FF" large class="mr-3">mdi-account-tie</v-icon>
          <h1 class="text-h5 font-weight-bold text--primary">{{ $t('oneOnOne.title') }}</h1>
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="12" sm="auto" class="pa-2">
        <v-btn v-if="!isViewOnlyMode" color="#00E5FF" height="40" depressed class="px-5 font-weight-bold action-btn black--text" style="border-radius: 8px;" @click="openScheduleDialog">
          <v-icon left color="black">mdi-plus</v-icon> {{ $t('oneOnOne.schedule') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="ma-n2 mb-2 top-cards-row">
      <v-col cols="12" md="4" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 top-card flex-grow-1">
          <v-card-text class="pa-5 d-flex flex-column justify-center align-start" style="height: 100%">
            <v-icon large color="#00E5FF" class="mb-3">mdi-calendar-clock</v-icon>
            <div class="text-caption text--secondary mb-1">{{ $t('oneOnOne.scheduled') }}</div>
            <div class="text-h3 font-weight-bold" style="color: #00E5FF;">{{ stats.scheduled }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 top-card flex-grow-1">
          <v-card-text class="pa-5 d-flex flex-column justify-center align-start" style="height: 100%">
            <v-icon large color="success" class="mb-3">mdi-check-circle-outline</v-icon>
            <div class="text-caption text--secondary mb-1">{{ $t('oneOnOne.completedThisMonth') }}</div>
            <div class="text-h3 success--text font-weight-bold">{{ stats.completed }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 top-card flex-grow-1">
          <v-card-text class="pa-5 d-flex flex-column justify-center align-start" style="height: 100%">
            <v-icon large color="#00E5FF" class="mb-3">mdi-chart-line</v-icon>
            <div class="text-caption text--secondary mb-1">{{ $t('oneOnOne.completionRate') }}</div>
            <div class="text-h3 font-weight-bold" style="color: #00E5FF;">{{ stats.completionRate }}%</div>
            <div class="text-caption text--secondary">{{ $t('oneOnOne.done') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row align="center" class="ma-n2 mb-4 mt-2">
      <v-col cols="auto" class="pa-2">
        <v-chip-group v-model="meetingFilter" mandatory>
          <v-chip value="all" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': meetingFilter === 'all'}">{{ $t('oneOnOne.filter.all') }}</v-chip>
          <v-chip value="pending" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': meetingFilter === 'pending'}">{{ $t('oneOnOne.filter.pending') }}</v-chip>
          <v-chip value="scheduled" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': meetingFilter === 'scheduled'}">{{ $t('oneOnOne.filter.scheduled') }}</v-chip>
          <v-chip value="cancelled" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': meetingFilter === 'cancelled'}">{{ $t('oneOnOne.filter.cancelled') }}</v-chip>
        </v-chip-group>
      </v-col>

      <v-col v-if="meetingFilter === 'scheduled' || meetingFilter === 'all'" cols="12" sm="auto" class="d-flex align-center pa-2">
        <v-divider vertical class="mx-3 theme-divider" style="height: 40px;" />
        <span class="text-body-2 font-weight-bold text--primary mr-3">{{ $t('oneOnOne.period') }}</span>
        
        <v-menu v-model="dateStartMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="dateStart" :label="$t('oneOnOne.startDate')" prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" dense outlined hide-details clearable @click:clear="dateStart = null" class="mr-3 elevment-input" color="#00E5FF" style="max-width: 160px;" />
          </template>
          <v-date-picker v-model="dateStart" @input="dateStartMenu = false" locale="pt-BR" :max="dateEnd || undefined" color="#00E5FF" />
        </v-menu>

        <v-menu v-model="dateEndMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="dateEnd" :label="$t('oneOnOne.endDate')" prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" dense outlined hide-details clearable @click:clear="dateEnd = null" class="elevment-input" color="#00E5FF" style="max-width: 160px;" />
          </template>
          <v-date-picker v-model="dateEnd" @input="dateEndMenu = false" locale="pt-BR" :min="dateStart || undefined" color="#00E5FF" />
        </v-menu>
      </v-col>
    </v-row>

    <v-row class="ma-n2">
      <v-col cols="12" class="pa-2">
        <v-card v-for="meeting in filteredMeetings" :key="meeting.id" class="elevment-card elevation-0 mb-3 meeting-list-card">
          <v-card-text class="pa-4">
            <v-row align="center" class="ma-0">
              <v-col cols="auto" class="pa-0 pr-4">
                <v-avatar size="48" :color="getAvatarColor(meeting.userName)" class="elevation-0">
                  <v-img v-if="meeting.userAvatar" :src="meeting.userAvatar" :alt="meeting.userName" />
                  <span v-else class="white--text font-weight-bold text-subtitle-1">
                    {{ getInitials(meeting.userName || 'Usuario') }}
                  </span>
                </v-avatar>
              </v-col>

              <v-col class="pa-0 pr-4">
                <div class="mb-2">
                  <div class="text-subtitle-1 font-weight-bold text--primary" style="line-height: 1.2;">
                    {{ meeting.userName || 'Colaborador' }}
                  </div>
                  <div class="text-caption text--secondary mt-1">
                    {{ meeting.userJobRole || 'Cargo não informado' }}
                  </div>
                </div>
                
                <div v-if="meeting.topics && meeting.topics.length > 0" class="d-flex flex-wrap gap-1 mt-2">
                  <v-chip v-for="(topic, index) in meeting.topics" :key="index" x-small outlined class="mr-1 mb-1 elevation-0 theme-chip-topic">
                    {{ topic }}
                  </v-chip>
                </div>
              </v-col>

              <v-col cols="auto" class="pa-0 pr-4">
                <div v-if="(meeting.status === 'SCHEDULED' || meeting.status === 'SCHEDULED_RESULT') && isMeetingToday(meeting)">
                  <v-btn :color="meeting.joinWebUrl ? '#6264A7' : 'warning'" height="32" depressed class="px-4 font-weight-bold action-btn black--text" style="border-radius: 6px; font-size: 12px; color: #FFFFFF;" :disabled="isViewOnlyMode" @click="startMeeting(meeting)">
                    <v-icon small left color="white">{{ meeting.joinWebUrl ? 'mdi-microsoft-teams' : 'mdi-video' }}</v-icon>
                    {{ meeting.joinWebUrl ? 'Entrar Teams' : 'Entrar' }}
                  </v-btn>
                </div>

                <div v-else-if="meeting.status === 'COMPLETED'">
                  <v-btn color="primary" height="32" small depressed outlined class="px-4 font-weight-bold action-btn-outlined" style="border-radius: 6px; font-size: 12px; border-color: rgba(128,128,128,0.3) !important;" @click="viewNotes(meeting)">
                    Ver Anotações
                  </v-btn>
                </div>
              </v-col>

              <v-col cols="auto" class="text-right pa-0 pr-3" style="min-width: 140px;">
                <v-chip outlined x-small class="elevation-0 theme-badge" :class="getStatusChipClass(meeting.status)">
                  {{ getStatusLabel(meeting.status) }}
                </v-chip>

                <div class="mt-2 mb-2">
                  <div class="text-subtitle-2 font-weight-bold text--primary">
                    {{ getMeetingDate(meeting) }}
                  </div>
                  <div class="d-flex align-center justify-end text-caption text--secondary mt-1">
                    <v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                    {{ getMeetingTime(meeting) }}
                  </div>
                </div>

                <div v-if="meeting.status === 'COMPLETED'">
                  <v-chip outlined x-small class="elevation-0 theme-badge warning--text">
                    <v-icon x-small left color="#FF771C">mdi-fire</v-icon>
                    +{{ meeting.xpReward || 50 }} XP
                  </v-chip>
                </div>
              </v-col>

              <v-col cols="auto" class="pa-0">
                <v-btn icon small color="grey" class="expand-btn" @click="toggleExpand(meeting.id)">
                  <v-icon>
                    {{ expandedMeeting === meeting.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-expand-transition>
            <div v-show="expandedMeeting === meeting.id">
              <v-divider class="theme-divider" />
              <v-card-text class="pa-5 expanded-area" :style="{ backgroundColor: $vuetify.theme.dark ? '#121212' : '#F5F5F7' }">
                <v-row>
                  <v-col cols="12" md="6" class="py-2">
                    <div class="d-flex align-center mb-2">
                      <v-icon color="#FF771C" small class="mr-2 icon-tonal-warning">mdi-account-tie</v-icon>
                      <span class="text-subtitle-2 font-weight-bold text--primary">Mentor</span>
                    </div>
                    <div class="d-flex align-center pl-8">
                      <v-avatar :color="getAvatarColor(manager?.name || 'Mentor')" size="36" class="mr-3 elevation-0">
                        <v-img v-if="manager?.avatarUrl" :src="manager.avatarUrl" />
                        <span v-else class="white--text text-caption font-weight-bold">{{ getInitials(manager?.name || 'Mentor') }}</span>
                      </v-avatar>
                      <div>
                        <div class="text-body-2 font-weight-bold text--primary">{{ manager?.name || 'Mentor' }}</div>
                        <div class="text-caption text--secondary">{{ manager?.jobRoleName || 'Cargo' }}</div>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6" class="py-2">
                    <div class="d-flex align-center mb-2">
                      <v-icon color="#FF771C" small class="mr-2 icon-tonal-warning">mdi-format-text</v-icon>
                      <span class="text-subtitle-2 font-weight-bold text--primary">Título / Tópico Principal</span>
                    </div>
                    <div class="text-body-2 pl-8 text--secondary">
                      {{ meeting.title || 'Sem título' }}
                    </div>
                  </v-col>

                  <v-col v-if="meeting.notes" cols="12" class="py-2">
                    <v-divider class="theme-divider my-2" />
                    <div class="d-flex align-center mb-2">
                      <v-icon color="#FF771C" small class="mr-2 icon-tonal-warning">mdi-note-text-outline</v-icon>
                      <span class="text-subtitle-2 font-weight-bold text--primary">Anotações</span>
                    </div>
                    <div class="text-body-2 pl-8 text--secondary" style="line-height: 1.5;">
                      {{ meeting.notes }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </div>
          </v-expand-transition>
        </v-card>

        <v-card v-if="meetings.length === 0 && !loading" class="pa-10 text-center elevment-card elevation-0">
          <v-icon size="64" color="rgba(128,128,128,0.2)">mdi-calendar-blank</v-icon>
          <h3 class="text-h6 text--primary mt-5">Nenhuma reunião agendada</h3>
          <p class="text-body-2 text--secondary mb-5">Solicite ou agende sua primeira reunião One-on-One com seu mentor.</p>
          <v-btn v-if="!isViewOnlyMode" color="#00E5FF" depressed @click="openScheduleDialog" class="font-weight-bold black--text">
            <v-icon left color="black">mdi-plus</v-icon> Agendar Primeira Reunião
          </v-btn>
        </v-card>

        <v-card v-if="loading" class="pa-10 text-center elevment-card elevation-0">
          <v-progress-circular indeterminate color="#00E5FF" size="48" />
          <p class="mt-4 text--secondary">Carregando reuniões...</p>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="scheduleDialog" max-width="560px" persistent>
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4 d-flex justify-space-between align-center">
          <h3 class="text-h5 font-weight-bold text--primary">Solicitar Agendamento</h3>
          <v-btn icon color="grey" @click="closeScheduleDialog" small><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider class="theme-divider" />

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="validForm">
              <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Mentor</div>
              <div class="d-flex align-center pa-3 elevment-box mb-4">
                  <v-avatar size="40" :color="getAvatarColor(manager?.name || 'Mentor')" class="mr-3 elevation-0">
                    <v-img v-if="manager?.avatarUrl" :src="manager.avatarUrl" />
                    <span v-else class="white--text text-caption font-weight-bold">{{ getInitials(manager?.name || 'Mentor') }}</span>
                  </v-avatar>
                  <div>
                      <div class="text-body-2 font-weight-bold text--primary">{{ manager?.name || 'Aguardando mentor...' }}</div>
                      <div class="text-caption text--secondary">{{ manager?.jobRoleName || 'Cargo' }}</div>
                  </div>
              </div>

              <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Título do One-on-One *</div>
              <v-text-field v-model="newMeeting.title" placeholder="Ex: Alinhamento de Metas, Feedback Trimestral" :rules="[v => !!v || 'Título é obrigatório']" outlined dense prepend-inner-icon="mdi-text" class="elevment-input mb-2" color="#00E5FF" hide-details />

            <div class="text-subtitle-2 font-weight-bold text--primary mb-2 mt-4">Período de Preferência *</div>
            <v-chip-group v-model="newMeeting.datePeriod" mandatory class="mb-2">
                <v-chip value="today" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': newMeeting.datePeriod === 'today'}">Hoje</v-chip>
                <v-chip value="this_week" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': newMeeting.datePeriod === 'this_week'}">Esta semana</v-chip>
                <v-chip value="this_month" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': newMeeting.datePeriod === 'this_month'}">Este mês</v-chip>
              </v-chip-group>

            <div class="text-subtitle-2 font-weight-bold text--primary mb-2 mt-4">Anotações / Agenda (Opcional)</div>
            <v-textarea v-model="newMeeting.notes" placeholder="Cite tópicos que gostaria de abordar ou envie o link da agenda" outlined dense rows="4" prepend-inner-icon="mdi-note-text-outline" class="elevment-input" color="#00E5FF" hide-details />
          </v-form>
        </v-card-text>

        <v-divider class="theme-divider" />
        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn text height="40" class="px-5 font-weight-bold text--secondary" style="border-radius: 8px;" @click="closeScheduleDialog">Cancelar</v-btn>
          <v-btn color="#00E5FF" height="40" depressed class="px-6 font-weight-bold black--text" style="border-radius: 8px;" :loading="saving" :disabled="isViewOnlyMode" @click="scheduleMeeting">
            <v-icon left color="black">mdi-calendar-check</v-icon> Solicitar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="notesDialog" max-width="600px">
      <v-card class="elevment-dialog-card elevation-0">
          <v-card-title class="pa-6 pb-4 d-flex justify-space-between align-center">
            <h3 class="text-h5 font-weight-bold text--primary">Anotações da Reunião</h3>
            <v-btn icon color="grey" @click="notesDialog = false" small><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-divider class="theme-divider" />

        <v-card-text class="pa-6">
          <div v-if="selectedMeeting">
              <div class="d-flex justify-space-between align-center mb-2">
                <v-chip outlined x-small class="elevation-0" :class="getStatusChipClass(selectedMeeting.status)">
                  {{ getStatusLabel(selectedMeeting.status) }}
                </v-chip>
                <div class="text-caption text--secondary">
                    <v-icon x-small color="grey" class="mr-1">mdi-calendar</v-icon>
                    {{ formatDateTime(selectedMeeting.scheduledAt) }}
                </div>
              </div>

              <h3 class="text-h6 font-weight-bold text--primary mb-4">{{ selectedMeeting.title || 'Reunião One-on-One' }}</h3>

            <div class="d-flex align-center pa-3 elevment-box mb-4">
                  <v-avatar size="36" color="primary" class="mr-3 elevation-0">
                    <v-img v-if="selectedMeeting.userAvatar" :src="selectedMeeting.userAvatar" />
                    <span v-else class="white--text text-caption font-weight-bold">{{ getInitials(selectedMeeting.userName) }}</span>
                  </v-avatar>
                  <div>
                      <div class="text-body-2 font-weight-bold text--primary">{{ selectedMeeting.userName || 'Aguardando mentor...' }}</div>
                      <div class="text-caption text--secondary">{{ selectedMeeting.userJobRole || 'Cargo' }}</div>
                  </div>
              </div>

            <div>
              <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Pontos Discutidos:</div>
              <div class="text-body-2 text--secondary pa-3 elevment-box" style="min-height: 100px; line-height: 1.6;">
                {{ selectedMeeting.notes || 'Nenhuma anotação registrada pelo mentor.' }}
              </div>
            </div>
          </div>
        </v-card-text>
        <v-divider class="theme-divider" />
        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn color="#00E5FF" depressed height="40" class="px-6 font-weight-bold black--text" style="border-radius: 8px;" @click="notesDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top elevation="0" rounded="lg">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false" color="white" class="font-weight-bold">Ok</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { OneOnOnesService, UsersService } from '@/services'

export default {
  name: 'OneOnOneUser',

  data: () => ({
    loading: false,
    meetings: [],
    manager: null,
    meetingFilter: 'all',
    dateStart: null,
    dateEnd: null,
    dateStartMenu: false,
    dateEndMenu: false,
    stats: {
      scheduled: 0,
      completed: 0,
      totalMeetings: 0,
      avgDuration: '45 min',
      streak: '6 semanas',
      completionRate: 0,
    },
    scheduleDialog: false,
    validForm: false,
    saving: false,
    newMeeting: {
      title: '',
      datePeriod: 'this_week',
      notes: '',
    },
    defaultMeeting: {
      title: '',
      datePeriod: 'this_week',
      notes: '',
    },
    notesDialog: false,
    selectedMeeting: null,
    expandedMeeting: null,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'primary',
  }),

  computed: {
    currentUser() {
      try {
        const userStr = localStorage.getItem('user')
        return userStr ? JSON.parse(userStr) : null
      } catch (error) {
        return null
      }
    },
    filteredMeetings() {
      let filtered = []
      if (this.meetingFilter === 'all') {
        filtered = this.meetings
      } else if (this.meetingFilter === 'pending') {
        filtered = this.meetings.filter(m => !m.scheduledAt && m.status !== 'CANCELLED')
      } else if (this.meetingFilter === 'scheduled') {
        filtered = this.meetings.filter(m => m.scheduledAt && m.status !== 'CANCELLED')
      } else if (this.meetingFilter === 'cancelled') {
        filtered = this.meetings.filter(m => m.status === 'CANCELLED')
      } else {
        filtered = this.meetings
      }

      if ((this.dateStart || this.dateEnd) && (this.meetingFilter === 'scheduled' || this.meetingFilter === 'all')) {
        filtered = filtered.filter(m => {
          if (!m.scheduledAt) return false
          const meetingDate = new Date(m.scheduledAt)
          const startDate = this.dateStart ? new Date(this.dateStart + 'T00:00:00') : null
          const endDate = this.dateEnd ? new Date(this.dateEnd + 'T23:59:59') : null
          
          if (startDate && endDate) return meetingDate >= startDate && meetingDate <= endDate
          else if (startDate) return meetingDate >= startDate
          else if (endDate) return meetingDate <= endDate
          return true
        })
      }

      if (this.meetingFilter === 'scheduled' || this.meetingFilter === 'all') {
        filtered = filtered.sort((a, b) => {
          if (!a.scheduledAt && !b.scheduledAt) return 0
          if (!a.scheduledAt) return 1
          if (!b.scheduledAt) return -1
          return new Date(a.scheduledAt) - new Date(b.scheduledAt)
        })
      }
      return filtered
    },
  },

  mounted() {
    this.loadMeetings()
    this.loadManager()
  },

  methods: {
    async loadMeetings() {
      this.loading = true
      try {
        const response = await OneOnOnesService.list()
        this.meetings = response.data
        this.calculateStats()
      } catch (error) {
        console.error('Erro ao carregar reuniões:', error)
        this.showSnackbar('Erro ao carregar reuniões', 'error')
      } finally {
        this.loading = false
      }
    },
    async loadManager() {
      try {
        const response = await UsersService.getMyManager()
        this.manager = response.data
      } catch (error) {
        console.error('Erro ao buscar mentor:', error)
      }
    },
    calculateStats() {
      this.stats.scheduled = this.meetings.filter(m => m.status === 'SCHEDULED' || m.status === 'SCHEDULED_RESULT').length
      const thisMonth = new Date().getMonth()
      const thisYear = new Date().getFullYear()
      
      this.stats.completed = this.meetings.filter(m => {
        if (m.status !== 'COMPLETED') return false
        if (!m.scheduledAt) return false
        const meetingDate = new Date(m.scheduledAt)
        return meetingDate.getMonth() === thisMonth && meetingDate.getFullYear() === thisYear
      }).length
      
      this.stats.totalMeetings = this.meetings.length
      this.stats.completionRate = this.stats.totalMeetings > 0
        ? Math.round((this.meetings.filter(m => m.status === 'COMPLETED').length / this.stats.totalMeetings) * 100)
        : 0
    },
    toggleExpand(meetingId) {
      this.expandedMeeting = this.expandedMeeting === meetingId ? null : meetingId
    },
    async openScheduleDialog() {
      this.newMeeting = { ...this.defaultMeeting }
      if (!this.manager) {
        try {
          const response = await UsersService.getMyManager()
          this.manager = response.data
        } catch (error) {
          console.error('Erro ao buscar mentor:', error)
          this.showSnackbar('Erro ao buscar mentor', 'error')
          return
        }
      }
      this.scheduleDialog = true
    },
    closeScheduleDialog() {
      this.scheduleDialog = false
      this.$refs.form?.reset()
    },
    async scheduleMeeting() {
      if (!this.$refs.form.validate()) return
      if (!this.manager) {
        this.showSnackbar('Mentor não encontrado', 'error')
        return
      }

      this.saving = true
      try {
        const intentionPeriodMap = {
          'today': 'TODAY',
          'this_week': 'THIS_WEEK',
          'this_month': 'THIS_MONTH'
        }
        const meetingData = {
          title: this.newMeeting.title,
          userId: this.currentUser?.id,
          managerId: this.manager.id,
          intentionPeriod: intentionPeriodMap[this.newMeeting.datePeriod],
          status: 'SCHEDULED',
          notes: this.newMeeting.notes,
        }
        await OneOnOnesService.schedule(meetingData)
        this.showSnackbar('Solicitação de agendamento enviada com sucesso!', 'success')
        this.closeScheduleDialog()
        this.loadMeetings()
      } catch (error) {
        console.error('Erro ao solicitar agendamento:', error)
        this.showSnackbar('Erro ao solicitar agendamento', 'error')
      } finally {
        this.saving = false
      }
    },
    startMeeting(meeting) {
      if (meeting.joinWebUrl) {
        window.open(meeting.joinWebUrl, '_blank')
        this.showSnackbar('Abrindo reunião no Teams...', 'primary')
      } else {
        this.showSnackbar('Link da reunião ainda não disponível. Solicite ao seu gestor.', 'warning')
      }
    },
    viewNotes(meeting) {
      this.selectedMeeting = meeting
      this.notesDialog = true
    },
    isMeetingToday(meeting) {
      if (!meeting.scheduledAt) return false
      const today = new Date()
      const meetingDate = new Date(meeting.scheduledAt)
      return today.toDateString() === meetingDate.toDateString()
    },
    getMeetingDate(meeting) {
      if (!meeting.scheduledAt && meeting.intentionPeriod) return this.translateIntentionPeriod(meeting.intentionPeriod)
      if (!meeting.scheduledAt) return '-'
      const date = new Date(meeting.scheduledAt)
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      if (date.toDateString() === today.toDateString()) return 'Hoje'
      else if (date.toDateString() === tomorrow.toDateString()) return 'Amanhã'
      else return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    },
    getMeetingTime(meeting) {
      if (!meeting.scheduledAt && meeting.intentionPeriod) return 'Aguardando'
      if (!meeting.scheduledAt) return '-'
      const date = new Date(meeting.scheduledAt)
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    },
    translateIntentionPeriod(intentionPeriod) {
      const translations = { 'TODAY': 'Hoje', 'THIS_WEEK': 'Esta semana', 'THIS_MONTH': 'Este mês' }
      return translations[intentionPeriod] || intentionPeriod
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      const date = new Date(dateTime)
      return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    getStatusLabel(status) {
      const labels = {
        SCHEDULED: 'Agendada',
        SCHEDULED_RESULT: 'Resultado PDI',
        IN_PROGRESS: 'Em Andamento',
        COMPLETED: 'Concluída',
        CANCELLED: 'Cancelada',
      }
      return labels[status] || status
    },
    getStatusChipClass(status) {
        const classes = {
          SCHEDULED: 'cyan--text',
          SCHEDULED_RESULT: 'purple--text',
          IN_PROGRESS: 'warning--text',
          COMPLETED: 'success--text',
          CANCELLED: 'grey--text',
        }
        return classes[status] || 'grey--text';
    },
    getAvatarColor(name) {
      const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2']
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },
    getInitials(name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },
    clearDateFilter() {
      this.dateStart = null
      this.dateEnd = null
    },
    showSnackbar(text, color = 'primary') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
  },
}
</script>

<style scoped>
.dashboard-container {
  max-width: 1440px;
  margin: 0 auto;
}

/* Custom Header */
.text-h5 {
  font-size: 1.5rem !important;
}
.text-h4 {
  font-family: 'Inter', 'Roboto', sans-serif !important;
  font-size: 1.5rem !important;
  font-weight: 600 !important;
  letter-spacing: -0.02em !important;
}

/* ========== Stats Cards (Premium SaaS) ========== */
.top-cards-row {
  align-items: stretch;
}

.top-card {
  min-height: 140px;
}

/* ========== Base elevment-card structure ========== */
.elevment-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease, border-color 0.2s ease;
  border: 1px solid rgba(128, 128, 128, 0.15) !important;
}

.theme--dark .elevment-card { background-color: #1A1A1A !important; }
.theme--light .elevment-card { background-color: #FFFFFF !important; }

/* Divisores Sutis */
.theme-divider {
  border-color: rgba(128, 128, 128, 0.15) !important;
}

/* ========== Filters Section (Chips Globais Delineados) ========== */

/* Tags (Chips) do Vuetify - Aplicando o estilo Pílula/Wireframe universalmente NO COMPONENTE */
::v-deep .v-chip {
  border-radius: 9999px !important;
  padding: 0 12px !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Delineado Padrão para os Filtros do Topo */
::v-deep .theme-chip-filter {
  background-color: transparent !important;
  border: 1px solid rgba(128, 128, 128, 0.2) !important;
  color: rgba(128, 128, 128, 0.7) !important;
}
.theme--dark ::v-deep .theme-chip-filter { 
  border-color: rgba(255, 255, 255, 0.1) !important; 
  color: rgba(255, 255, 255, 0.6) !important; 
}

/* Filtro Ativo */
::v-deep .theme-chip-filter.active {
  border-color: #00E5FF !important;
  color: #00E5FF !important;
  background-color: rgba(0, 229, 255, 0.05) !important;
}

/* Tópicos Simples (Anotações) */
::v-deep .theme-chip-topic {
  background-color: transparent !important;
  border: 1px solid rgba(128, 128, 128, 0.2) !important;
  color: rgba(128, 128, 128, 0.7) !important;
  text-transform: none; /* Textos de tópicos não precisam ser caixa alta */
  font-weight: 500 !important;
  font-size: 0.7rem !important;
}
.theme--dark ::v-deep .theme-chip-topic { border-color: rgba(255, 255, 255, 0.1) !important; color: rgba(255, 255, 255, 0.6) !important; }

/* ========== Cores Delineadas Baseada nas Classes de Texto ========== */
::v-deep .v-chip.cyan--text { border-color: #00E5FF !important; color: #00E5FF !important; background-color: transparent !important; }
::v-deep .v-chip.success--text { border-color: #34C759 !important; color: #34C759 !important; background-color: transparent !important; }
::v-deep .v-chip.warning--text { border-color: #FF771C !important; color: #FF771C !important; background-color: transparent !important; }
::v-deep .v-chip.purple--text { border-color: #9C27B0 !important; color: #9C27B0 !important; background-color: transparent !important; }
::v-deep .v-chip.grey--text { border-color: rgba(128, 128, 128, 0.3) !important; color: rgba(128, 128, 128, 0.7) !important; background-color: transparent !important; }
.theme--dark ::v-deep .v-chip.grey--text { border-color: rgba(255, 255, 255, 0.2) !important; color: rgba(255, 255, 255, 0.6) !important; }


/* ========== OVERRIDES DOS INPUTS (Filtros) ========== */
.theme--dark .elevment-input ::v-deep .v-input__control > .v-input__slot {
  background-color: #1A1A1A !important;
}
.theme--dark .elevment-input ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
.theme--dark .elevment-input:hover ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.theme--light .elevment-input ::v-deep .v-input__control > .v-input__slot {
  background-color: #FFFFFF !important;
}
.theme--light .elevment-input ::v-deep fieldset {
  border-color: rgba(0, 0, 0, 0.12) !important;
}

/* ========== Meeting List Card visual enhancements ========== */
.meeting-list-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
}
.theme--light .meeting-list-card:hover {
  border-color: rgba(0, 0, 0, 0.15) !important;
}
.theme--dark .meeting-list-card:hover {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.action-btn { letter-spacing: 0 !important; }

.action-btn-outlined {
    letter-spacing: 0 !important;
    border-color: rgba(128, 128, 128, 0.3) !important;
}

/* ========== Expanded Area (RESOLVE DARK MODE) ========== */
.expanded-area {
  border-radius: 0 0 12px 12px;
}

/* Icon Box Tonal Ajustado - REMOVIDO para bater com Imagem 1, agora usamos ícones grandes diretos */
.icon-tonal-warning {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  position: relative;
  background-color: transparent;
  opacity: 1 !important;
}
.icon-tonal-warning::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: #FF771C;
    opacity: 0.12;
    border-radius: 8px;
}

/* Dialogs */
.elevment-dialog-card {
  border-radius: 12px !important;
  border: 1px solid rgba(128, 128, 128, 0.15) !important;
}
.theme--dark .elevment-dialog-card { background-color: #1A1A1A !important; }
.theme--light .elevment-dialog-card { background-color: #FFFFFF !important; }

.elevment-box {
  background-color: rgba(128, 128, 128, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(128, 128, 128, 0.1);
}

/* Custom Scrollbar */
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-thumb { background: rgba(128, 128, 128, 0.3); border-radius: 3px; }
::-webkit-scrollbar-track { background: transparent; }
</style>